import { useIsClient } from "@uidotdev/usehooks";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formCompanyQueryParam } from "../../../functions/company";
import { decodePage, encodePage } from "../../../helpers/filter_management";
import {
  clearCompanyList,
  getCompanyList,
} from "../../../redux/actions/company_action";
import SharedPagination from "../../shared/SharedPagination/SharedPagination";
import CompanyCard from "../CompanyCard/CompanyCard";
import CompanyFilter from "../CompanyFilter/CompanyFilter";
import CompanyNoResults from "../CompanyNoResults/CompanyNoResults";
import CompanyCardSkeleton from "../Skeleton/CompanyCardSkeleton";
import { CompanyListContainer, Wrapper } from "./styles";

export default function CompanyList(props) {
  const { serverCompanyList, serverFilterParams, serverPageParams } = props;

  const dispatch = useDispatch();
  const router = useRouter();

  const isClient = useIsClient();

  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [renderedCompanyList, setRenderedCompanyList] =
    useState(serverCompanyList);

  // Filter Related Items
  const companyFilter = useSelector((state) => state.companies.companyFilter);

  // Company Related Items
  const fetchingCompanies = useSelector(
    (state) => state.companies.fetchingCompanyList
  );
  const companyList = useSelector((state) => state.companies.companies);

  // Pagination Related Items
  const totalPages = useSelector((state) => state.companies.totalCompaniesPage);
  const companyPageInfo = useSelector(
    (state) => state.companies.companyPageInfo
  );

  const currentPage = !companyPageInfo?.hasNextPage
    ? decodePage(companyPageInfo?.endCursor, 20) + 1
    : decodePage(companyPageInfo?.endCursor, 20);

  const isFetchingCompany = fetchingCompanies || companiesLoading;

  function loadCompanyList(refresh = false, companyFilter, endCursor = null) {
    setCompaniesLoading(true);
    dispatch(clearCompanyList());

    if (!refresh) {
      scrollTo(0, 700);
    }

    const params = formCompanyQueryParam(companyFilter, refresh);

    const paginationParams = {
      first: 20,
      last: null,
      before: null,
      after: endCursor,
    };

    const combinedParams = {
      ...params,
      ...paginationParams,
    };

    dispatch(getCompanyList(combinedParams));
  }

  function companyListPreviousPage() {
    companyListPageChange(currentPage - 1);
  }

  function companyListNextPage() {
    companyListPageChange(currentPage + 1);
  }

  function companyListPageChange(page) {
    // apiPage is the page number that will be used to fetch the data from the api
    const apiPage = page - 1;
    // actualPage is the page number that will be used to display the page number
    const actualPage = page;

    router.push(
      {
        pathname: "/companies",
        query: {
          ...router.query,
          page: actualPage,
        },
        scroll: false,
      },
      undefined,
      { shallow: true }
    );

    loadCompanyList(false, companyFilter, encodePage(apiPage, 20));
  }

  useEffect(() => {
    if (isClient) {
      setRenderedCompanyList(companyList);
    }

    if (!fetchingCompanies) {
      setCompaniesLoading(false);
    }
  }, [isClient, companyList, fetchingCompanies]);

  useEffect(() => {
    if (isClient) {
      loadCompanyList(true, companyFilter, serverPageParams);
    } else {
      loadCompanyList(true, serverFilterParams, serverPageParams);
    }
  }, [companyFilter]);

  return (
    <Wrapper>
      <CompanyFilter serverFilterParams={serverFilterParams} />
      {isFetchingCompany ? (
        <CompanyListContainer>
          {Array.from({ length: 20 }).map((_, index) => (
            <CompanyCardSkeleton key={index} />
          ))}
        </CompanyListContainer>
      ) : renderedCompanyList.length > 0 && companyList.length > 0 ? (
        <>
          <CompanyListContainer>
            {renderedCompanyList.map((company) => {
              return <CompanyCard key={company?.id} company={company} />;
            })}
          </CompanyListContainer>

          <SharedPagination
            currentPage={currentPage}
            totalPages={totalPages}
            isLoading={isFetchingCompany}
            onPageChange={companyListPageChange}
            onNextPage={companyListNextPage}
            onPreviousPage={companyListPreviousPage}
          />
        </>
      ) : (
        <CompanyNoResults />
      )}
    </Wrapper>
  );
}
